import { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { Loader2, UserPlus, Camera, Check, X as XIcon } from 'lucide-react';
import { tryGetLocation } from '@/lib/construction-offline';

const SKILLS = ['helper', 'electrician', 'foreman', 'grinder', 'mason', 'painter', 'plumber', 'other'] as const;
type Skill = (typeof SKILLS)[number];

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  projectId: string;
}

export function AddWorkerSheet({ open, onClose, onCreated, projectId }: Props) {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('+966');
  const [skill, setSkill] = useState<Skill>('helper');
  const [hourlyRate, setHourlyRate] = useState('12');
  const [iqama, setIqama] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFullName('');
    setPhone('+966');
    setSkill('helper');
    setHourlyRate('12');
    setIqama('');
    setPhotoFile(null);
    setPhotoPreview(null);
    setError(null);
  };

  // Free the preview blob URL when photo changes or sheet closes
  useEffect(() => {
    if (!photoPreview) return;
    return () => URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  function pickPhoto(f: File) {
    setPhotoFile(f);
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
  }

  function clearPhoto() {
    setPhotoFile(null);
    setPhotoPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    if (fileRef.current) fileRef.current.value = '';
  }

  const handleSubmit = async () => {
    setError(null);
    if (fullName.trim().length < 2) { setError('Enter the full name (at least 2 characters)'); return; }
    const phoneTrim = phone.trim();
    if (phoneTrim.replace(/[^0-9]/g, '').length < 9) { setError('Phone number is required (9+ digits)'); return; }
    const rate = Number(hourlyRate);
    if (!Number.isFinite(rate) || rate <= 0) { setError('Hourly rate must be greater than 0'); return; }
    const iqamaTrim = iqama.trim();
    if (iqamaTrim && !/^[0-9]{8,12}$/.test(iqamaTrim)) { setError('Iqama must be 8–12 digits (or leave blank)'); return; }
    if (!photoFile) { setError('Please take a photo of the worker'); return; }

    setBusy(true);
    try {
      // 1. Create the worker record
      const workerRes = await apiClient.post<{ id: string; fullName: string }>(
        `/api/v1/construction/projects/${projectId}/workers`,
        {
          fullName: fullName.trim(),
          skill,
          hourlyRate: rate,
          phone: phoneTrim,
          iqamaNumber: iqamaTrim || null,
        },
      );
      const newWorker = workerRes.data;

      // 2. Upload the joining-day profile photo
      const fd = new FormData();
      fd.append('file', photoFile);
      fd.append('workerId', newWorker.id);
      const gps = await tryGetLocation();
      if (gps) {
        fd.append('lat', String(gps.lat));
        fd.append('lng', String(gps.lng));
      }
      const jwt = getStoredToken();
      const photoRes = await fetch(
        `${API_BASE}/api/v1/construction-photos/projects/${projectId}/worker-profile`,
        { method: 'POST', headers: jwt ? { Authorization: `Bearer ${jwt}` } : {}, body: fd },
      );
      if (!photoRes.ok) {
        // Worker was created but photo failed — surface the issue but don't roll back the worker
        const txt = await photoRes.text().catch(() => '');
        throw new Error(`Worker created but photo upload failed: ${txt || photoRes.statusText}`);
      }

      reset();
      onCreated();
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add worker
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="add-fullName">Full name</Label>
            <Input
              id="add-fullName"
              autoFocus
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Ali Hassan"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-phone">Phone</Label>
            <Input
              id="add-phone"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+9665XXXXXXXX"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-skill">Skill</Label>
            <select
              id="add-skill"
              value={skill}
              onChange={(e) => setSkill(e.target.value as Skill)}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base capitalize ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {SKILLS.map((s) => (
                <option key={s} value={s} className="capitalize">{s}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-rate">Hourly rate (SAR)</Label>
            <Input
              id="add-rate"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Joining-day photo</Label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) pickPhoto(f);
                e.target.value = '';
              }}
            />
            {photoPreview ? (
              <div className="flex items-center gap-3 rounded-md border border-border p-2">
                <img src={photoPreview} alt="" className="h-16 w-16 rounded object-cover bg-muted" />
                <div className="flex-1 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                  <Check className="h-3.5 w-3.5" /> Ready to upload
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={clearPhoto} aria-label="Remove photo">
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2 border-dashed h-20"
                onClick={() => fileRef.current?.click()}
              >
                <Camera className="h-5 w-5" />
                Take photo
              </Button>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-iqama" className="flex items-center gap-2">
              Iqama number
              <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="add-iqama"
              inputMode="numeric"
              value={iqama}
              onChange={(e) => setIqama(e.target.value)}
              placeholder="10-digit number"
            />
            <p className="text-xs text-muted-foreground">Required for site access — add later if not yet issued.</p>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm p-3">
              {error}
            </div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={busy}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={busy} className="gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Add worker
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
